import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Mail, MenuIcon, X, ArrowRight, Target, MessageSquare, DollarSign, Eye } from "lucide-react"; // , ArrowLeft
import { NavigationMenu, NavigationMenuList, NavigationMenuItem } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { toast } from "@/components/ui/use-toast";
import MediaGallery from '@/components/MediaGallery';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Button as AriaButton,
  CalendarCell,
  CalendarGrid,
  DateInput,
  DateRangePicker,
  DateSegment,
  Dialog,
  Group,
  Heading,
  Popover,
  RangeCalendar
} from 'react-aria-components';
import { CalendarDate, today, getLocalTimeZone } from '@internationalized/date';
import type { DateRange } from 'react-aria-components';
// import useEmblaCarousel from 'embla-carousel-react';
// import Autoplay from 'embla-carousel-autoplay';

// Define section IDs
const SECTION_IDS = {
  HOME: "home",
  BENEFITS: "benefits",
  GALLERY: "gallery",
  CONTACT: "contact",
  FAQ: "faq",
};

const Index = () => {
  const [activeSection, setActiveSection] = useState<string>(SECTION_IDS.HOME);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [animatedSections, setAnimatedSections] = useState<{[key: string]: boolean}>({});
  const [dateRange, setDateRange] = useState<DateRange | null>({
    start: new CalendarDate(new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate()),
    end: new CalendarDate(new Date().getFullYear(), new Date().getMonth() + 2, new Date().getDate())
  });
  const [armadaSize, setArmadaSize] = useState<number[]>([1]);
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  // Form field states
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [source, setSource] = useState("");
  const [details, setDetails] = useState("");

  // Refs for sections that will be animated
  const benefitsSectionRef = useRef<HTMLElement>(null);
  const gallerySectionRef = useRef<HTMLElement>(null);
  const trustedBySectionRef = useRef<HTMLElement>(null);
  const faqSectionRef = useRef<HTMLElement>(null);
  const contactSectionRef = useRef<HTMLElement>(null);
  const newsletterSectionRef = useRef<HTMLElement>(null);
  const heroSectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  // Function to reset all form fields
  const resetForm = () => {
    setName("");
    setPhone("");
    setEmail("");
    setWebsite("");
    setSelectedLocation("");
    setDateRange({
      start: new CalendarDate(new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate()),
      end: new CalendarDate(new Date().getFullYear(), new Date().getMonth() + 2, new Date().getDate())
    });
    setArmadaSize([1]);
    setSource("");
    setDetails("");
  };

  useEffect(() => {
    // Create Intersection Observer to detect when sections enter viewport
    const observerOptions = {
      root: null, // viewport
      rootMargin: '0px',
      threshold: 0.1 // trigger when 10% of the section is visible
    };

    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          setAnimatedSections(prev => ({ ...prev, [sectionId]: true }));
        }
      });
    }, observerOptions);

    // Observe all sections that need animations
    const sections = [
      heroSectionRef.current,
      benefitsSectionRef.current,
      gallerySectionRef.current,
      trustedBySectionRef.current,
      faqSectionRef.current,
      contactSectionRef.current,
      newsletterSectionRef.current
    ];

    sections.forEach(section => {
      if (section) sectionObserver.observe(section);
    });

    return () => {
      sections.forEach(section => {
        if (section) sectionObserver.unobserve(section);
      });
    };
  }, []);

  // Effect to update activeSection based on scroll position
  useEffect(() => {
    const observerOptions = { 
      root: null, 
      rootMargin: '-50% 0px -50% 0px', 
      threshold: 0 
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          // Only update if it's a navigation section (Home, Benefits, Gallery, Contact, FAQ)
          if (['home', 'benefits', 'gallery', 'contact', 'faq'].includes(sectionId)) {
            setActiveSection(sectionId);
          }
        }
      });
    }, observerOptions);

    // Observe all sections
    const sections = document.querySelectorAll('section[id]');
    sections.forEach(section => {
      observer.observe(section);
    });

    return () => {
      sections.forEach(section => {
        observer.unobserve(section);
      });
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setActiveSection(sectionId);
    setMobileMenuOpen(false);
  };

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const location = formData.get('location') as string;
    if (!location) {
      toast({
        title: "Location Required",
        description: "Please select a location.",
        variant: "destructive",
      });
      return;
    }
    const data = {
      location,
      name: formData.get('name') as string,
      phone: formData.get('phone') as string,
      email: formData.get('email') as string,
      website: formData.get('website') as string,
      timeline_start: dateRange?.start ? dateRange.start.toString() : undefined,
      timeline_end: dateRange?.end ? dateRange.end.toString() : undefined,
      source: formData.get('source') as string,
      details: formData.get('details') as string,
      armada_size: armadaSize[0],
    };

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to submit form');
      }

      toast({
        title: "Quote Request Sent",
        description: "We'll be in touch with you shortly.",
        variant: "default",
      });
      // Reset form fields
      resetForm();
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Submission Failed",
        description: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive",
      });
    }
  };

  // Handle date range changes from the picker
  const handleDateRangeChange = (value: DateRange | null) => {
    setDateRange(value);
  };

  // Handle newsletter form submission
  const handleNewsletterSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const data = {
      name: formData.get('newsletter-name') as string,
      email: formData.get('newsletter-email') as string,
    };

    try {
      const response = await fetch('/api/subscribe-newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to subscribe to newsletter');
      }

      toast({
        title: "Subscribed!",
        description: "You've been added to our newsletter.",
        variant: "default",
      });

      // Reset the form
      form.reset();
      
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast({
        title: "Subscription Failed",
        description: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background pt-16">
      {/* Navigation */}
      <header
        ref={headerRef}
        className="fixed top-0 left-0 right-0 z-[999] w-full border-b border-primary/20 bg-background"
      >
        <div className="container flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="MEDIA TRUCK Logo" className="h-10" />
            {/* <span className="text-xl font-bold text-gradient">MEDIA TRUCK</span> */}
          </div>

          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
          </Button>

          {/* Desktop Navigation */}
          <NavigationMenu className="hidden lg:flex">
            <NavigationMenuList>
              <NavigationMenuItem>
                <Button
                  variant="ghost"
                  className={cn(
                    "relative nav-link",
                    activeSection === SECTION_IDS.HOME && "text-primary font-medium"
                  )}
                  onClick={() => scrollToSection(SECTION_IDS.HOME)}
                >
                  Home
                  {activeSection === SECTION_IDS.HOME && (
                    <span className="absolute -bottom-[1.5px] left-0 h-0.5 w-full bg-primary" />
                  )}
                </Button>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <Button
                  variant="ghost"
                  className={cn(
                    "relative nav-link",
                    activeSection === SECTION_IDS.BENEFITS && "text-primary font-medium"
                  )}
                  onClick={() => scrollToSection(SECTION_IDS.BENEFITS)}
                >
                  Benefits
                  {activeSection === SECTION_IDS.BENEFITS && (
                    <span className="absolute -bottom-[1.5px] left-0 h-0.5 w-full bg-primary" />
                  )}
                </Button>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <Button
                  variant="ghost"
                  className={cn(
                    "relative nav-link",
                    activeSection === SECTION_IDS.GALLERY && "text-primary font-medium"
                  )}
                  onClick={() => scrollToSection(SECTION_IDS.GALLERY)}
                >
                  Gallery
                  {activeSection === SECTION_IDS.GALLERY && (
                    <span className="absolute -bottom-[1.5px] left-0 h-0.5 w-full bg-primary" />
                  )}
                </Button>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <Button
                  variant="ghost"
                  className={cn(
                    "relative nav-link",
                    activeSection === SECTION_IDS.CONTACT && "text-primary font-medium"
                  )}
                  onClick={() => scrollToSection(SECTION_IDS.CONTACT)}
                >
                  Contact
                  {activeSection === SECTION_IDS.CONTACT && (
                    <span className="absolute -bottom-[1.5px] left-0 h-0.5 w-full bg-primary" />
                  )}
                </Button>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <Button
                  variant="ghost"
                  className={cn(
                    "relative nav-link",
                    activeSection === SECTION_IDS.FAQ && "text-primary font-medium"
                  )}
                  onClick={() => scrollToSection(SECTION_IDS.FAQ)}
                >
                  FAQ
                  {activeSection === SECTION_IDS.FAQ && (
                    <span className="absolute -bottom-[1.5px] left-0 h-0.5 w-full bg-primary" />
                  )}
                </Button>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          <Button
            onClick={() => scrollToSection(SECTION_IDS.CONTACT)}
            variant="outline"
            className="hidden lg:inline-flex border-primary/30 hover:bg-primary/5 hover:border-primary/50 hover-scale glow"
          >
            Get Started
          </Button>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 top-16 z-50 bg-background border-t border-primary/20">
            <nav className="container py-8 flex flex-col space-y-6">
              <Button
                variant="ghost"
                size="lg"
                className={cn("w-full justify-start text-lg font-medium",
                  activeSection === SECTION_IDS.HOME && "bg-primary/10 text-primary"
                )}
                onClick={() => scrollToSection(SECTION_IDS.HOME)}
              >
                Home
              </Button>
              <Button
                variant="ghost"
                size="lg"
                className={cn("w-full justify-start text-lg font-medium",
                  activeSection === SECTION_IDS.BENEFITS && "bg-primary/10 text-primary"
                )}
                onClick={() => scrollToSection(SECTION_IDS.BENEFITS)}
              >
                Benefits
              </Button>
              <Button
                variant="ghost"
                size="lg"
                className={cn("w-full justify-start text-lg font-medium",
                  activeSection === SECTION_IDS.GALLERY && "bg-primary/10 text-primary"
                )}
                onClick={() => scrollToSection(SECTION_IDS.GALLERY)}
              >
                Gallery
              </Button>
              <Button
                variant="ghost"
                size="lg"
                className={cn("w-full justify-start text-lg font-medium",
                  activeSection === SECTION_IDS.CONTACT && "bg-primary/10 text-primary"
                )}
                onClick={() => scrollToSection(SECTION_IDS.CONTACT)}
              >
                Contact
              </Button>
              <Button
                variant="ghost"
                size="lg"
                className={cn("w-full justify-start text-lg font-medium",
                  activeSection === SECTION_IDS.FAQ && "bg-primary/10 text-primary"
                )}
                onClick={() => scrollToSection(SECTION_IDS.FAQ)}
              >
                FAQ
              </Button>
              <Button
                onClick={() => {
                  scrollToSection(SECTION_IDS.CONTACT);
                  setMobileMenuOpen(false);
                }}
                size="lg"
                className="w-full btn-primary-dark"
              >
                Get Started
              </Button>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">
        {/* Hero Section */}
        <section id={SECTION_IDS.HOME} ref={heroSectionRef} className="relative px-4 py-20 md:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-linear-to-b from-primary/10 to-transparent pointer-events-none"></div>
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-[40%] -right-[10%] w-[70%] h-[80%] bg-primary/5 rounded-full blur-3xl"></div>
            <div className="absolute top-[60%] -left-[10%] w-[50%] h-[60%] bg-primary/5 rounded-full blur-3xl"></div>
          </div>

          <div className="container grid md:grid-cols-2 gap-12 items-center relative">
            <div className="space-y-8">
              <div className={`transition-all duration-1000 ${animatedSections[SECTION_IDS.HOME] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <h2 className="text-4xl md:text-6xl text-white font-bold mb-2 leading-tight">
                  DIGITAL TRANSIT ADVERTISING
                </h2>
                <h1 className="text-2xl md:text-4xl font-bold leading-tight text-gradient">
                  <span className="block">Your Brand, On the Road</span>
                </h1>
                <p className="text-lg mt-2 text-muted-foreground">
                  We take your message directly to your audience neighborhood by neighborhood, block by block.
                </p>
              </div>

              <div className={`flex flex-col sm:flex-row gap-4 transition-all duration-1000 delay-300 ${animatedSections[SECTION_IDS.HOME] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => scrollToSection(SECTION_IDS.CONTACT)}
                  className="border-primary/30 hover:bg-primary/5 hover:border-primary/50 hover-scale glow"
                >
                  Get Started <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className={`relative h-64 md:h-96 transition-all duration-1000 delay-300 ${animatedSections[SECTION_IDS.HOME] ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
                <img 
                  src="/images/truck.avif" 
                  alt="Digital Transit Advertising Truck"
                  className="object-contain w-full h-full"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section - Placeholder */}
        <section 
          id={SECTION_IDS.BENEFITS} 
          ref={benefitsSectionRef}
          className="py-20 px-4 bg-transparent"
        >
          <div className="container text-center">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 text-gradient transition-all duration-1000 ${animatedSections[SECTION_IDS.BENEFITS] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              Why Choose Media Truck?
            </h2>
            <p className={`text-lg text-muted-foreground mb-12 max-w-3xl mx-auto transition-all duration-1000 delay-150 ${animatedSections[SECTION_IDS.BENEFITS] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              Leverage the power of mobile digital advertising with unique advantages.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { icon: Target, title: "Targeted Placement", description: "We can geofence specific locations, deploy media trucks to relevant conferences, and reach your audience where they are." },
                { icon: MessageSquare, title: "Dynamic Messaging", description: "Deliver your message effectively through a combination of still images, video, and audio with 100% SOP compliance." },
                { icon: DollarSign, title: "Cost Effective", description: "Reach a large audience with a lower cost-per-impression compared to traditional billboards." },
                { icon: Eye, title: "Grab Audience's Attention", description: "Stand out from the clutter with bright, moving displays that capture eyeballs." }
              ].map((benefit, index) => (
                <Card 
                  key={index} 
                  className={`glass border-primary/20 text-left p-6 hover:shadow-lg transition-all duration-1000 delay-${300 + index * 150} ${animatedSections[SECTION_IDS.BENEFITS] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} hover:border-primary/40 hover:-translate-y-1`}
                >
                  <div className="flex flex-col items-start h-full">
                    <div className="mb-4 bg-primary/10 p-3 rounded-full">
                      <benefit.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                    <p className="text-muted-foreground flex-grow">{benefit.description}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Media Gallery Section */}
        <section
          id={SECTION_IDS.GALLERY}
          ref={gallerySectionRef}
          className="py-20 pt-10 px-4 inset-0 bg-linear-to-t from-primary/5 to-transparent"
        >
          <div className="container">
            <h2 className={`text-3xl md:text-4xl font-bold mb-12 text-center text-gradient transition-all duration-1000 ${animatedSections[SECTION_IDS.GALLERY] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              Our Trucks in Action
            </h2>
            <div className={`transition-all duration-1000 delay-300 ${animatedSections[SECTION_IDS.GALLERY] ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
              <MediaGallery />
            </div>
          </div>
        </section>

        {/* Trusted By Section - Placeholder */}
        {/* <section 
          id="trusted-by"
          ref={trustedBySectionRef} 
          className="py-20 px-4 inset-0 bg-linear-to-b from-primary/5 to-transparent"
        >
          <div className="container text-center">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 text-gradient transition-all duration-1000 ${animatedSections["trusted-by"] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              Trusted by 100+ Industry Leaders
            </h2>
            <p className={`text-lg text-muted-foreground mb-8 max-w-2xl mx-auto transition-all duration-1000 delay-150 ${animatedSections["trusted-by"] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              Join successful brands that leverage Media Truck for impactful advertising.
            </p>
            <div className={`transition-all duration-1000 delay-300 ${animatedSections["trusted-by"] ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
              <ClientCarousel />
            </div>
          </div>
        </section> */}

        {/* Newsletter Signup Section bg-transparent */}
        <section 
          id="newsletter" 
          ref={newsletterSectionRef}
          className="py-16 px-4 inset-0 bg-linear-to-b from-primary/5 to-transparent"
        >
          <div className="container max-w-xl mx-auto text-center">
            <div className={`flex justify-center items-center gap-3 mb-3 transition-all duration-1000 ${animatedSections["newsletter"] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <Mail className="h-7 w-7 text-primary" />
              <h2 className="text-3xl font-bold text-gradient">Stay Updated</h2>
            </div>
            <p className={`text-muted-foreground mb-8 transition-all duration-1000 delay-150 ${animatedSections["newsletter"] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              Subscribe to our newsletter for the latest news and offers.
            </p>
            <form
              className={`flex flex-col sm:flex-row gap-3 max-w-lg mx-auto transition-all duration-1000 delay-300 ${animatedSections["newsletter"] ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
              onSubmit={handleNewsletterSubmit}
            >
              <Input
                type="text"
                name="newsletter-name"
                placeholder="Your Name"
                required
                className="flex-grow bg-background/70 border-primary/20 focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0"
                aria-label="Your Name"
              />
              <Input
                type="email"
                name="newsletter-email"
                placeholder="your@email.com"
                required
                className="flex-grow bg-background/70 border-primary/20 focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0"
                aria-label="Your Email"
              />
              <Button variant="outline" type="submit" className="border-primary/30 hover:bg-primary/5 hover:border-primary/50 hover-scale glow shrink-0">
                Subscribe
              </Button>
            </form>
          </div>
        </section>

        {/* Contact Section */}
        <section 
          id={SECTION_IDS.CONTACT} 
          ref={contactSectionRef}
          className="py-20 px-4 relative"
        >
          <div className="absolute inset-0 bg-linear-to-t from-primary/5 to-transparent pointer-events-none"></div>
          <div className="container">
            <div className="grid md:grid-cols-2 gap-12">
              <div className="space-y-6">
                <span className={`inline-block bg-primary/20 text-primary px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-1000 ${animatedSections[SECTION_IDS.CONTACT] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                  Get In Touch
                </span>
                <h2 className={`text-3xl md:text-4xl font-bold text-gradient transition-all duration-1000 delay-150 ${animatedSections[SECTION_IDS.CONTACT] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                  Ready to Brand Your Fleet?
                </h2>
                <p className={`text-lg text-muted-foreground transition-all duration-1000 delay-300 ${animatedSections[SECTION_IDS.CONTACT] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                  Our team of experts is ready to help you create the perfect branded fleet solution for your business.
                </p>

                <div className={`space-y-6 mt-8 transition-all duration-1000 delay-450 ${animatedSections[SECTION_IDS.CONTACT] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                  {/* <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <Phone className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">Give us a call</p>
                      <p className="text-muted-foreground">(555) 123-4567</p>
                    </div>
                  </div> */}
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <Mail className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">Email us</p>
                      <p className="text-muted-foreground">info@mediatruck.com</p>
                    </div>
                  </div>
                  {/* <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <Users className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">Visit our team</p>
                      <p className="text-muted-foreground">123 Media Avenue, Truckville</p>
                    </div>
                  </div> */}
                </div>
              </div>

              <Card className={`glass border-primary/20 overflow-hidden transition-all duration-1000 ${animatedSections[SECTION_IDS.CONTACT] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
                <CardContent className="pt-6">
                  <form className="space-y-6" onSubmit={handleFormSubmit}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-foreground block mb-2">Name</Label>
                        <Input
                          id="name"
                          name="name"
                          placeholder="John Doe"
                          required
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="bg-secondary/50 border-primary/20 focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-foreground block mb-2">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="(555) 123-4567"
                          required
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className="bg-secondary/50 border-primary/20 focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-foreground block mb-2">Email</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="john@example.com"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="bg-secondary/50 border-primary/20 focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="website" className="text-foreground block mb-2">Website</Label>
                        <Input
                          id="website"
                          name="website"
                          placeholder="www.example.com"
                          value={website}
                          onChange={(e) => setWebsite(e.target.value)}
                          className="bg-secondary/50 border-primary/20 focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col lg:flex-row gap-4 [&>*]:w-full lg:[&>*]:w-auto">
                      <div className="space-y-2 lg:flex-1">
                        <Label htmlFor="location" className="text-foreground block mb-2">Interested Location</Label>
                        <Select
                          name="location"
                          defaultValue=""
                          value={selectedLocation}
                          onValueChange={setSelectedLocation}
                        >
                          <SelectTrigger className="w-full bg-secondary/50 border-primary/20 focus:ring-1 focus:ring-primary focus:ring-offset-0">
                            <SelectValue placeholder="Select a city" />
                          </SelectTrigger>
                          <SelectContent className="bg-secondary border-primary/20" style={{ position: 'absolute' }}>
                            <SelectItem value="Los Angeles" className="focus:bg-primary/30 focus:text-primary-foreground">Los Angeles</SelectItem>
                            <SelectItem value="Las Vegas" className="focus:bg-primary/30 focus:text-primary-foreground">Las Vegas</SelectItem>
                            <SelectItem value="New York" className="focus:bg-primary/30 focus:text-primary-foreground">New York</SelectItem>
                            <SelectItem value="Miami" className="focus:bg-primary/30 focus:text-primary-foreground">Miami</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2 lg:flex-1 lg:min-w-[239px]">
                        <Label htmlFor="timeline" className="text-foreground block mb-2">Campaign Timeline</Label>
                        <DateRangePicker aria-label="Campaign Timeline"
                          value={dateRange}
                          onChange={handleDateRangeChange}
                          className="w-full text-center"
                          minValue={today(getLocalTimeZone())}
                        >
                          <Group className="flex items-center border border-primary/20 rounded-md px-2 py-2 bg-secondary/50 focus-within:ring-1 focus-within:ring-primary focus-within:border-primary/40 overflow-hidden">
                            <DateInput slot="start" className="flex-1 text-xs sm:text-sm truncate">
                              {(segment: any) => <DateSegment segment={segment} className="px-0.5 py-0.5 text-xs sm:text-sm focus:bg-primary/20 focus:outline-none rounded" />}
                            </DateInput>
                            <span aria-hidden="true" className="mx-1 text-muted-foreground shrink-0">–</span>
                            <DateInput slot="end" className="flex-1 text-xs sm:text-sm truncate">
                              {(segment: any) => <DateSegment segment={segment} className="px-0.5 py-0.5 text-xs sm:text-sm focus:bg-primary/20 focus:outline-none rounded" />}
                            </DateInput>
                            <AriaButton className="ml-1 text-primary hover:text-primary/80 shrink-0">▼</AriaButton>
                          </Group>
                          <Popover className="bg-card p-4 rounded-md shadow-xl border border-primary/20 mt-1">
                            <Dialog>
                              <RangeCalendar>
                                <header className="flex justify-between items-center mb-4">
                                  <AriaButton slot="previous" className="p-1.5 rounded-full hover:bg-primary/10 text-primary">◀</AriaButton>
                                  <Heading className="text-lg font-medium" />
                                  <AriaButton slot="next" className="p-1.5 rounded-full hover:bg-primary/10 text-primary">▶</AriaButton>
                                </header>
                                <CalendarGrid className="border-collapse">
                                  {(date: CalendarDate) => <CalendarCell date={date} className="p-2 text-center data-[outside-month]:text-gray-400 data-[unavailable]:text-gray-300 data-[unavailable]:line-through hover:bg-primary/10 focus:bg-primary/20 focus:outline-none cursor-pointer data-[selected]:bg-primary/30 data-[selected]:text-primary rounded-md" />}
                                </CalendarGrid>
                              </RangeCalendar>
                            </Dialog>
                          </Popover>
                        </DateRangePicker>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="source" className="text-foreground block mb-2">How did you hear about Media Truck?</Label>
                      <Input
                        id="source"
                        name="source"
                        placeholder="e.g., Google Search, LinkedIn, Referral"
                        value={source}
                        onChange={(e) => setSource(e.target.value)}
                        className="bg-secondary/50 border-primary/20 focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0 pb-1"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="details" className="text-foreground block mb-2">Campaign Details</Label>
                      <Textarea
                        id="details"
                        name="details"
                        value={details}
                        onChange={(e) => setDetails(e.target.value)}
                        className="resize-none flex h-32 w-full rounded-md border border-primary/20 bg-secondary/50 px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                        placeholder="Tell us about your campaign goals, target audience, etc."
                        required
                      />
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="armada-size" className="text-foreground">Armada Size</Label>
                        <div className="px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-sm font-medium relative -top-[2px]">
                          {armadaSize[0]}
                        </div>
                      </div>
                      <div className="relative pb-8">
                        <Slider
                          id="armada-size"
                          defaultValue={[1]}
                          min={1}
                          max={5}
                          step={1}
                          value={armadaSize}
                          onValueChange={setArmadaSize}
                          className="relative [&>span:first-child]:h-1.5 [&>span:first-child]:bg-primary/30 [&_[role=slider]]:bg-primary [&_[role=slider]]:w-5 [&_[role=slider]]:h-5 [&_[role=slider]]:border-0 focus-visible:ring-1 focus-visible:ring-primary/50 focus-visible:ring-offset-0"
                        />
                        {/* Slider marks */}
                        <div className="absolute bottom-0 left-0 right-0 flex justify-between px-2">
                          {[1, 2, 3, 4, 5].map((mark) => (
                            <div key={mark} className="flex flex-col items-center">
                              <div className="h-1.5 w-0.5 bg-primary/30 mb-1" />
                              <span className="text-xs text-muted-foreground">{mark}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <input type="hidden" name="armada_size_val" value={armadaSize[0]} />
                    </div>

                    <Button
                      type="submit"
                      className="w-full border-primary/30 hover:bg-primary/5 hover:border-primary/50 hover-scale glow"
                      variant="outline"
                    >
                      Send Inquiry <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section - Placeholder */}
        <section 
          id={SECTION_IDS.FAQ} 
          ref={faqSectionRef}
          className="py-20 px-4 relative"
        >
          <div className="absolute inset-0 bg-linear-to-b from-primary/5 to-transparent pointer-events-none"></div>
          <div className="container mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="space-y-6">
                <h2 className={`text-3xl md:text-4xl font-bold text-gradient transition-all duration-1000 ${animatedSections[SECTION_IDS.FAQ] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                  Frequently Asked Questions
                </h2>
                <p className={`text-lg text-muted-foreground transition-all duration-1000 delay-150 ${animatedSections[SECTION_IDS.FAQ] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                  Find answers to common questions about our digital billboard services. If you don't see your question answered here, feel free to contact our team directly.
                </p>
                <div className={`hidden lg:block transition-all duration-1000 delay-300 ${animatedSections[SECTION_IDS.FAQ] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                  <Button
                    onClick={() => scrollToSection(SECTION_IDS.CONTACT)}
                    className="border-primary/30 hover:bg-primary/5 hover:border-primary/50 hover-scale glow"
                    variant="outline"
                  >
                    Ask Us Anything <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <Accordion type="single" collapsible className={`w-full space-y-4 transition-all duration-1000 delay-300 ${animatedSections[SECTION_IDS.FAQ] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
                {[
                  { q: "What is Digital Billboard Advertising?", a: "Digital billboard advertising uses large digital screens (like LED displays) placed in high-traffic areas to showcase dynamic advertisements, including static images, videos, and changing messages." },
                  { q: "How Much Does Digital Billboard Advertising Cost?", a: "Costs vary widely based on location, screen size, duration, and ad complexity. Mobile digital billboards like ours offer competitive pricing compared to static locations. Contact us for a specific quote." },
                  { q: "How Does Digital Billboard Advertising Work?", a: "Advertisers create digital content displayed on screens managed by billboard owners or networks. Ads can be scheduled, targeted geographically, and updated remotely." },
                  { q: "How to Build an Advertising Digital Billboard?", a: "Building one involves significant investment in hardware (LED panels, controllers), software, location permits, and installation. Partnering with a provider like Media Truck is usually more practical." },
                  { q: "How Do I Find an Advertising Company to Manage Digital Billboard?", a: "Search online directories, industry associations (like OAAA), or look for companies specializing in Digital Out-of-Home (DOOH) advertising. Media Truck offers end-to-end campaign management." },
                  { q: "How can I book a digital billboard campaign with Bulldog Billboards?", a: "While we are Media Truck, booking typically involves contacting the billboard provider (like Bulldog Billboards or us), discussing your goals, providing artwork, and agreeing on terms and schedule." },
                  { q: "How Do Companies Use Partnerships for Digital Advertising?", a: "Partnerships can involve co-branding on ads, sponsoring mobile routes near partner events, or cross-promoting services to expand reach and credibility." },
                  { q: "How Much Does It Cost to Set Up a Digital Billboard?", a: "Setting up a *static* digital billboard can cost tens to hundreds of thousands of dollars. Mobile options like Media Truck require booking the truck service rather than building infrastructure." },
                  { q: "Are Digital Billboards Worth It?", a: "Yes, for many businesses. They offer high visibility, flexibility, dynamic content capabilities, and can deliver strong ROI, especially mobile billboards that can target specific areas." },
                  { q: "What types of digital billboard trucks does Media Truck offer?", a: "Media Truck offers state-of-the-art trucks equipped with high-resolution LED screens on multiple sides, capable of displaying static images, video, and audio." },
                  { q: "Where does Media Truck operate?", a: "We currently operate in major metropolitan areas. Please contact us with your specific location needs to confirm service availability." },
                  { q: "Can I customize the route for my digital billboard campaign?", a: "Absolutely! We work with you to define custom routes, geofenced zones, or target specific events and locations to maximize your campaign's relevance and impact." },
                  { q: "What makes Media Truck unique in digital billboard advertising?", a: "Media Truck combines mobility, high-impact display technology, route customization, geofencing capabilities, and dedicated support to deliver uniquely targeted and flexible advertising campaigns." }
                ].map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border border-primary/20 rounded-lg px-6 bg-secondary/30 glass hover:bg-secondary/50 transition-colors duration-200">
                    <AccordionTrigger className="text-left text-lg font-medium hover:no-underline text-foreground py-4 hover:text-primary transition-colors duration-200 flex justify-between items-start">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-base text-muted-foreground pb-4">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

      </main>

      <footer className="border-t border-primary/20 py-12 px-4 relative">
        <div className="absolute inset-0 bg-linear-to-t from-transparent to-primary/5 pointer-events-none"></div>
        
        <div className="container flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center gap-2 mb-6 md:mb-0">
            <img src="/logo.png" alt="MEDIA TRUCK Logo" className="h-10" />
            {/* <span className="text-xl font-bold text-gradient">MEDIA TRUCK</span> */}
          </div>

          <div className="flex flex-col items-center mb-6 md:mb-0">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} MEDIA TRUCK. All rights reserved.
            </p>
          </div>

          <div className="flex gap-4">
            {["linkedin", "twitter", "facebook"].map((social) => (
              <Button key={social} variant="ghost" size="icon" className="rounded-full hover:bg-primary/10 hover:text-primary">
                <span className="sr-only">{social}</span>
                {social === "linkedin" ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 8C17.5913 8 19.1174 8.63214 20.2426 9.75736C21.3679 10.8826 22 12.4087 22 14V21H18V14C18 13.4696 17.7893 12.9609 17.4142 12.5858C17.0391 12.2107 16.5304 12 16 12C15.4696 12 14.9609 12.2107 14.5858 12.5858C14.2107 12.9609 14 13.4696 14 14V21H10V14C10 12.4087 10.6321 10.8826 11.7574 9.75736C12.8826 8.63214 14.4087 8 16 8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M6 9H2V21H6V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4 6C5.10457 6 6 5.10457 6 4C6 2.89543 5.10457 2 4 2C2.89543 2 2 2.89543 2 4C2 5.10457 2.89543 6 4 6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : social === "twitter" ? (
                  <svg width="20" height="20" viewBox="0 0 300 300.251" fill="#fff" xmlns="http://www.w3.org/2000/svg">
                    <path d="M178.57 127.15 290.27 0h-26.46l-97.03 110.38L89.34 0H0l117.13 166.93L0 300.25h26.46l102.4-116.59 81.8 116.59h89.34M36.01 19.54H76.66l187.13 262.13h-40.66" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 2H15C13.6739 2 12.4021 2.52678 11.4645 3.46447C10.5268 4.40215 10 5.67392 10 7V10H7V14H10V22H14V14H17L18 10H14V7C14 6.73478 14.1054 6.48043 14.2929 6.29289C14.4804 6.10536 14.7348 6 15 6H18V2ZM12 19.37C8.76 19.37 5.96 17.6 4.73 14.89L7.9 11.98C8.49 13.8 10.09 15.13 12 15.13C13.06 15.13 13.97 14.74 14.7 14.09L16.92 16.2C15.65 17.38 14.01 19.37 12 19.37Z"/></svg>
                )}
              </Button>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

// Client carousel component
// const ClientCarousel = () => {
//   const [emblaRef, emblaApi] = useEmblaCarousel({ 
//     loop: true,
//     dragFree: true,
//     containScroll: "trimSnaps",
//     slidesToScroll: 1
//   }, [
//     Autoplay({ delay: 3000, stopOnInteraction: false })
//   ]);

//   const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
//   const scrollNext = () => emblaApi && emblaApi.scrollNext();

//   const clients = [
//     { name: "Facebook", logo: "/images/clients/facebook.png" },
//     { name: "Google", logo: "/images/clients/google.png" },
//     { name: "Pinterest", logo: "/images/clients/pinterest.png" },
//     { name: "Twitch", logo: "/images/clients/twitch.png" },
//     { name: "YouTube", logo: "/images/clients/youtube.png" },
//     { name: "Facebook", logo: "/images/clients/facebook.png" },
//     { name: "Google", logo: "/images/clients/google.png" },
//     { name: "Pinterest", logo: "/images/clients/pinterest.png" },
//     { name: "Twitch", logo: "/images/clients/twitch.png" },
//     { name: "YouTube", logo: "/images/clients/youtube.png" },
//     { name: "Facebook", logo: "/images/clients/facebook.png" },
//     { name: "Google", logo: "/images/clients/google.png" },
//     { name: "Pinterest", logo: "/images/clients/pinterest.png" },
//     { name: "Twitch", logo: "/images/clients/twitch.png" },
//     { name: "YouTube", logo: "/images/clients/youtube.png" },
//     { name: "Facebook", logo: "/images/clients/facebook.png" },
//     { name: "Google", logo: "/images/clients/google.png" },
//     { name: "Pinterest", logo: "/images/clients/pinterest.png" },
//     { name: "Twitch", logo: "/images/clients/twitch.png" },
//     { name: "YouTube", logo: "/images/clients/youtube.png" },
//   ];

//   return (
//     <div className="relative max-w-[720px] m-auto">
//       <div className="embla overflow-hidden" ref={emblaRef}>
//         <div className="embla__container flex">
//           {clients.map((client, index) => (
//             <div key={`${client.name}-${index}`} className="embla__slide flex-[0_0_33.3%] min-w-0 md:flex-[0_0_20%] px-4">
//               <div className="h-16 flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity duration-300 filter grayscale hover:grayscale-0">
//                 <img 
//                   src={client.logo} 
//                   alt={`${client.name} logo`} 
//                   className="max-h-8 w-auto object-contain"
//                 />
//               </div>
//             </div>
//           ))}
//         </div>
//       </div>
      
//       {/* Navigation Arrows */}
//       <Button 
//         onClick={scrollPrev}
//         className="absolute left-[-40px] md:left-[-35px] top-1/2 -translate-y-1/2 z-10 bg-background/80 hover:bg-background border-primary/30 rounded-full p-2 shadow-md"
//         size="icon"
//         variant="ghost"
//       >
//         <ArrowLeft className="h-5 w-5 text-primary" />
//       </Button>
//       <Button 
//         onClick={scrollNext}
//         className="absolute right-[-40px] md:right-[-35px] top-1/2 -translate-y-1/2 z-10 bg-background/80 hover:bg-background border-primary/30 rounded-full p-2 shadow-md"
//         size="icon"
//         variant="ghost"
//       >
//         <ArrowRight className="h-5 w-5 text-primary" />
//       </Button>
//     </div>
//   );
// };

export default Index;
